// =============================================================================
// app/src/lib/listas.ts — as regras puras de FILTRAR e ORDENAR uma lista.
//
// POR QUE ISTO EXISTE (pedido do dono, 01/09/2026): "melhorar a ordenação e o
// filtro de encomendas, vendas, etc.". Seis telas precisavam da mesma coisa —
// uma caixa de busca e um seletor de ordem — e cada uma ia inventar a sua. A
// tela de Estoque já tinha o desenho certo (busca + <select> de ordem); o que
// faltava era ele morar num lugar só, com teste, em vez de ser copiado.
//
// Nada de Supabase nem de React aqui: filtrar e ordenar é decisão determinística
// sobre dados que já chegaram, e é testável linha a linha — a mesma régua de
// encomenda.ts e orcamento.ts.
//
// O QUE ESTE ARQUIVO NÃO FAZ: recortar o que a pessoa pode VER. Quem faz isso é
// a RLS, no banco. Aqui só se reorganiza o que o banco já entregou.
// =============================================================================

/** NFD separa a letra do acento; a faixa apagada são só os acentos soltos.
 *  (Mesmo idioma de csv.ts — a régua do acento é uma só no app.) */
const ACENTOS = /[̀-ͯ]/g;

/**
 * O texto como se procura no balcão: sem acento, sem caixa, sem espaço sobrando.
 *
 * Sem isto, "jose" não acha "José" e "orcamento" não acha "Orçamento" — e quem
 * está no balcão com o cliente na frente não vai parar para descobrir onde fica
 * o acento no teclado. Vale para os dois lados: normaliza o que a pessoa digita
 * E o que está guardado.
 */
export function normalizar(texto: string | null | undefined): string {
  return (texto ?? '')
    .normalize('NFD').replace(ACENTOS, '')
    .toLowerCase()
    .trim();
}

/**
 * A linha casa com o que foi digitado?
 *
 * PALAVRA A PALAVRA, e todas precisam aparecer (E, não OU): "jose gol" acha
 * "José da Silva — Gol 1.0" e não devolve todo cliente chamado José. É como se
 * busca de verdade — a pessoa vai ACRESCENTANDO palavra até a lista encurtar —
 * e é o que buscar_pecas() já faz no banco.
 *
 * A ordem das palavras não importa, e nem em qual campo cada uma cai: "gol
 * jose" e "jose gol" devolvem a mesma linha. Termo vazio casa com tudo — a
 * busca em branco não pode esvaziar a tela.
 */
export function casaBusca(termo: string, ...campos: Array<string | null | undefined>): boolean {
  const palavras = normalizar(termo).split(/\s+/).filter(Boolean);
  if (palavras.length === 0) return true;
  const alvo = campos.map(normalizar).join(' ');
  // "dot4" tem de achar "DOT 4", e "5w-30" tem de achar "5W 30": quem digita
  // no balcão junta o que a etiqueta separa (11/09/2026, o dono na aba de
  // vendas). Por isso cada palavra é procurada também na versão SEM NADA ENTRE
  // AS LETRAS — dos campos e dela mesma. É a mesma régua de texto_busca() no
  // banco: o filtro da tela e a busca de peças têm de concordar sobre o que
  // casa, senão a peça aparece numa lista e some da outra.
  const compacto = alvo.replace(/[^a-z0-9]/g, '');
  return palavras.every((p) => {
    if (alvo.includes(p)) return true;
    const pc = p.replace(/[^a-z0-9]/g, '');
    return pc !== '' && compacto.includes(pc);
  });
}

/**
 * Comparador de texto em português: 'Ácido' vem antes de 'Barro', e 'a' antes
 * de 'B'. `localeCompare` sozinho já faz isso; o que se ganha aqui é o
 * tratamento do nulo — linha sem nome vai para o FIM, nunca para o topo, em
 * qualquer ordem alfabética.
 */
export function porTexto(a: string | null | undefined, b: string | null | undefined): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return a.localeCompare(b, 'pt-BR');
}

/**
 * Comparador de número com nulo no fim, na ordem pedida.
 *
 * O nulo NÃO é zero: uma encomenda "a combinar" não é uma encomenda de R$ 0,00,
 * e ordenar por valor não pode enfiá-la no meio das baratas. Ela vai para o
 * fim, tanto na crescente quanto na decrescente — é linha que falta resolver,
 * não linha de valor extremo.
 */
export function porNumero(
  a: number | null | undefined,
  b: number | null | undefined,
  direcao: 'asc' | 'desc' = 'asc',
): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return direcao === 'asc' ? a - b : b - a;
}

/**
 * Comparador de data (ISO, 'YYYY-MM-DD' ou timestamp completo), nulo no fim.
 *
 * Comparação de STRING é comparação de data no ISO, e de propósito: sem objeto
 * `Date` não há fuso para errar — a mesma escolha de situacaoEncomenda().
 */
export function porData(
  a: string | null | undefined,
  b: string | null | undefined,
  direcao: 'asc' | 'desc' = 'desc',
): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return direcao === 'asc' ? a.localeCompare(b) : b.localeCompare(a);
}
